import { Alert, Col, Form, FormInstance, Image, Row, Typography } from 'antd';
import { FunctionComponent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

// components
import PhoneInputComponent, { PhoneInputReturn } from 'components/phoneInput/phoneInput';
import PhoneViewComponent from 'components/phoneInput/phoneView';
import SelectElement from 'components/selectElement/selectElement';
import FormElement from 'components/formElement/formElement';
import FileInput from 'components/fileInput/fileInput';

// services
import { checkPhoneValidation } from 'utils/validators/phone';

// models
import { checkArabicValidation } from 'utils/validators/arabic';
import { checkEnglishValidation } from 'utils/validators/english';
import DisabledElement from 'components/disabledElement/disabledElement';
import { CommitteeMember } from '../models/members';
import { onGetCommitteeMemberByNationalId } from '../services/memberServices';

interface CommitteeMemberFormProps {
	form: FormInstance;
	showOnly?: boolean;
	secretaryOnly?: boolean;
	onSubmit?(data: CommitteeMember, newOne: boolean): void;
}

const CommitteeMemberForm: FunctionComponent<CommitteeMemberFormProps> = ({ form, onSubmit, showOnly, secretaryOnly }) => {
	const { t } = useTranslation();

	const [showAlert, setShowAlert] = useState(false);
	const [memberExist, setMemberExist] = useState(false);
	const [newMember, setNewMember] = useState(false);

	const [typeOptions, setTypeOptions] = useState<{ label: string; value: string }[]>([
		{ label: t('ACTIONS.EXECUTIVE'), value: 'Executive' },
		{ label: t('ACTIONS.INDEPENDENT'), value: 'Independent' },
		{ label: t('ACTIONS.NON_EXECUTIVE'), value: 'Non-Executive' },
		{ label: t('ACTIONS.STAFF'), value: 'Staff' },
	]);

	useEffect(() => {
		if (form.getFieldValue('name')) setMemberExist(true);

		if (secretaryOnly) {
			form.setFieldsValue({ position: 'Committee-Secretary', type: 'Staff' });
			setTypeOptions([{ label: t('ACTIONS.STAFF'), value: 'Staff' }]);
		}
	}, []);

	const validateArabic = (_: any, value: string) => {
		if (!value || memberExist) return Promise.resolve();
		if (checkArabicValidation(_, value)) {
			return Promise.resolve();
		} else if (value) return Promise.reject(new Error(t('COMMON.ARABIC_ONLY')));
		else return Promise.reject();
	};

	const validateEnglish = (_: any, value: string) => {
		if (!value || memberExist) return Promise.resolve();
		if (checkEnglishValidation(_, value)) {
			return Promise.resolve();
		} else if (value) return Promise.reject(new Error(t('COMMON.ENGLISH_ONLY')));
		else return Promise.reject();
	};

	const validatePhone = (_: any, value: any) => {
		const number = form.getFieldValue('phone_number');
		if (checkPhoneValidation(form.getFieldValue('phone_country_code'), number)) {
			return Promise.resolve();
		} else if (value) return Promise.reject(new Error(t('COMMON.PHONE_INVALID')));
		else return Promise.reject();
	};

	const onChangePhone = ({ number, dialCode }: PhoneInputReturn) => {
		form.setFieldsValue({
			phone_number: number,
			phone_country_code: dialCode,
		});
	};

	const changePositionHandler = (value: string) => {
		if (value === 'Committee-Secretary') {
			form.setFieldsValue({ type: 'Staff' });
			setTypeOptions([{ label: t('ACTIONS.STAFF'), value: 'Staff' }]);
		}

		if (value === 'Committee-chair') {
			form.setFieldsValue({ type: null });
			setTypeOptions([
				{ label: t('ACTIONS.INDEPENDENT'), value: 'Independent' },
				{ label: t('ACTIONS.NON_EXECUTIVE'), value: 'Non-Executive' },
			]);
		}

		if (value === 'Vice-Chairman' || value === 'Member') {
			form.setFieldsValue({ type: null });
			setTypeOptions([
				{ label: t('ACTIONS.EXECUTIVE'), value: 'Executive' },
				{ label: t('ACTIONS.INDEPENDENT'), value: 'Independent' },
				{ label: t('ACTIONS.NON_EXECUTIVE'), value: 'Non-Executive' },
			]);
		}
	};

	const getMemberByNationalId = async (nationalId: string) => {
		try {
			if (nationalId.length < 10) {
				setMemberExist(false);
				setShowAlert(false);
				form.resetFields(['name', 'name_en', 'email', 'phone_number', 'phone_country_code']);
				return;
			}
			const response = await onGetCommitteeMemberByNationalId(nationalId);
			form.setFieldsValue(response);
			setShowAlert(response.show_alert);
			setMemberExist(true);
			setNewMember(false);
		} catch (error) {
			setNewMember(true);
		}
	};

	return (
		<Form form={form} onFinish={(values) => onSubmit?.(values, newMember)} className="my-4">
			<Form.Item name="id" hidden noStyle />

			<Row gutter={[16, 16]}>
				<Col span={24} md={12}>
					<FormElement
						name="national_id"
						disabled={showOnly}
						type="number"
						label={t('ACTIONS.MEMBER_NATIONAL_ID')}
						fullWidth
						rules={[
							{ required: true, message: t('COMMON.FIELD_REQUIRED') },
							{ len: 10, message: t('COMMON.NATIONAL_ID_LENGTH') },
						]}
						onChange={(value) => getMemberByNationalId(value)}
					/>
				</Col>

				<Col span={24} md={12}>
					{showOnly || memberExist ? (
						<>
							<PhoneViewComponent
								label={t('ACTIONS.MEMBER_PHONE_NUMBER')}
								phoneNumber={form.getFieldValue('phone_number')}
								dialCode={form.getFieldValue('phone_country_code')}
							/>
							<Form.Item name="phone_number" hidden noStyle />
						</>
					) : (
						<Form.Item name="phone_number" rules={[{ required: !memberExist, message: t('COMMON.PHONE_REQUIRED') }, { validator: validatePhone }]}>
							<PhoneInputComponent
								onChange={onChangePhone}
								label={t('ACTIONS.MEMBER_PHONE_NUMBER')}
								userPhoneNumber={form.getFieldValue('phone_number')}
								userDialCode={form.getFieldValue('phone_country_code')}
							/>
						</Form.Item>
					)}
					<Form.Item name="phone_country_code" hidden noStyle />
				</Col>

				<Col span={24} md={12}>
					<FormElement
						name="name"
						disabled={showOnly || memberExist}
						type="text"
						label={t('ACTIONS.MEMBER_NAME_AR')}
						fullWidth
						rules={[{ required: !memberExist, message: t('COMMON.FIELD_REQUIRED') }, { validator: validateArabic }]}
					/>
				</Col>

				<Col span={24} md={12}>
					<FormElement
						name="name_en"
						disabled={showOnly || memberExist}
						type="text"
						label={t('ACTIONS.MEMBER_NAME_EN')}
						fullWidth
						rules={[{ required: !memberExist, message: t('COMMON.FIELD_REQUIRED') }, { validator: validateEnglish }]}
					/>
				</Col>

				<Col span={24} md={12}>
					<FormElement
						name="email"
						disabled={showOnly || memberExist}
						label={t('ACTIONS.MEMBER_EMAIL')}
						type="email"
						rules={[
							{ required: !memberExist, message: t('COMMON.FIELD_REQUIRED') },
							{ type: 'email', message: t('COMMON.EMAIL_INVALID') },
						]}
					/>
				</Col>

				<Col span={24} md={12}>
					{secretaryOnly ? (
						<>
							<DisabledElement value={t('ACTIONS.SECRETARY')} label={t('ACTIONS.MEMBER_POSITION')} />
							<Form.Item name="position" hidden noStyle />
						</>
					) : (
						<SelectElement
							name="position"
							disabled={showOnly}
							label={t('ACTIONS.MEMBER_POSITION')}
							rules={[{ required: true, message: t('COMMON.FIELD_REQUIRED') }]}
							options={[
								{ label: t('ACTIONS.COMMITTEE_CHAIRMAN'), value: 'Committee-chair' },
								{ label: t('ACTIONS.COMMITTEE_VICE_CHAIRMAN'), value: 'Vice-Chairman' },
								{ label: t('ACTIONS.MEMBER'), value: 'Member' },
								{ label: t('ACTIONS.SECRETARY'), value: 'Committee-Secretary' },
							]}
							onChange={changePositionHandler}
						/>
					)}
				</Col>

				<Col span={24} md={12}>
					<SelectElement
						name="type"
						disabled={showOnly}
						label={t('ACTIONS.MEMBER_TYPE')}
						rules={[{ required: true, message: t('COMMON.FIELD_REQUIRED') }]}
						options={typeOptions}
					/>
				</Col>

				{showOnly && form.getFieldValue('signature')?.download_link && (
					<Col span={24} md={12}>
						<Typography.Text style={{ marginBottom: 5, display: 'block', fontSize: 15, textAlign: 'start' }}>
							{t('ACTIONS.SIGNATURE')}
						</Typography.Text>
						<Image src={form.getFieldValue('signature').download_link} style={{ width: 120, height: 120 }} />
					</Col>
				)}

				{showAlert && (
					<Col span={24} className="mt-4">
						<Alert message={t('ACTIONS.MEMBER_ALERT')} type="error" />
					</Col>
				)}
			</Row>
		</Form>
	);
};

export default CommitteeMemberForm;
